/**
 * Mapeo de Tipos para GeocodingOrchestrator
 * 
 * TD-002.F.3: Funciones de mapeo entre sistemas de tipología
 * 
 * @module services/geocoding/orchestrator/typeMapping
 * @version 1.0.0
 */

import { InfrastructureType } from '../../../types/infrastructure';
import { type InfraTipologia } from '../../../lib/localData/schemas';
import { type InfrastructureCategory } from '../../../lib/LocalDataService';

// ============================================================================
// MAPEO INFRASTRUCTURETYPE → INFRATIPOLOGIA (SingletonDetector)
// ============================================================================

/**
 * B.4: Mapea InfrastructureType a InfraTipologia para SingletonDetector
 * 
 * @param type - Tipo de infraestructura del clasificador
 * @returns Tipología para consulta en BBDD local
 */
export function mapTypeToTipologia(type: InfrastructureType): InfraTipologia {
  switch (type) {
    case InfrastructureType.HEALTH:
      return 'SANITARIO';
    case InfrastructureType.EDUCATION:
      return 'EDUCATIVO';
    case InfrastructureType.POLICE:
      return 'SEGURIDAD';
    case InfrastructureType.FIRE:
      return 'BOMBEROS';
    case InfrastructureType.EMERGENCY:
      return 'EMERGENCIA';
    // UPCLS3 eslabón 40 · MAREMOTO se mapea a la macro-categoría EMERGENCIA
    // (decisión TUN1 macro cerrado 19 categorías). El detalle granular se
    // preserva en `properties.tipoOriginal === 'PUNTO_ENCUENTRO_MAREMOTO'`.
    case InfrastructureType.MAREMOTO:
      return 'EMERGENCIA';
    case InfrastructureType.MUNICIPAL:
      return 'MUNICIPAL';
    case InfrastructureType.ENERGY:
      return 'ENERGIA';
    case InfrastructureType.HYDRAULIC:
      return 'HIDRAULICO';
    case InfrastructureType.CULTURAL:
    case InfrastructureType.RELIGIOUS:  // FIX #1: Edificios religiosos = patrimonio cultural
      return 'PATRIMONIO';
    case InfrastructureType.SPORTS:
      return 'DEPORTIVO';
    case InfrastructureType.VIAL:
    case InfrastructureType.PORT:  // FIX #1: Puertos = infraestructura transporte
      return 'TRANSPORTE';
    case InfrastructureType.RSU:
      return 'RESIDUOS';
    // Tipos sin tipología DERA específica → OTRO
    case InfrastructureType.TELECOM:
    case InfrastructureType.INDUSTRIAL:
    case InfrastructureType.FUEL:
    case InfrastructureType.SOCIAL:
    case InfrastructureType.GENERIC:
    default:
      return 'OTRO';
  }
}

// ============================================================================
// MAPEO INFRASTRUCTURETYPE → LOCALCATEGORIES (LocalDataService)
// ============================================================================

/**
 * Mapea InfrastructureType a categorías LocalDataService
 * Usado por L0 (LOCAL_DERA) para filtrar búsqueda
 * 
 * @param type - Tipo de infraestructura
 * @returns Array de categorías para búsqueda local
 */
export function mapTypeToLocalCategories(type: InfrastructureType): InfrastructureCategory[] {
  switch (type) {
    case InfrastructureType.HEALTH:
      return ['health'];
    
    case InfrastructureType.EDUCATION:
      return ['education'];
    
    case InfrastructureType.POLICE:
    case InfrastructureType.FIRE:
    case InfrastructureType.EMERGENCY:
    // UPCLS3 eslabón 40 · MAREMOTO comparte categoría local con EMERGENCY:
    // no existe categoría LocalDataService específica para maremoto/litoral,
    // y la búsqueda en `'security'` es la más afín semánticamente.
    case InfrastructureType.MAREMOTO:
      return ['security'];
    
    case InfrastructureType.ENERGY:
      return ['energy'];
    
    case InfrastructureType.MUNICIPAL:
      return ['municipal'];
    
    // Tipos sin datos locales específicos: buscar en todo
    case InfrastructureType.CULTURAL:
    case InfrastructureType.SPORTS:
    case InfrastructureType.HYDRAULIC:
    case InfrastructureType.TELECOM:
    case InfrastructureType.VIAL:
    case InfrastructureType.INDUSTRIAL:
    case InfrastructureType.GENERIC:
    default:
      return ['health', 'security', 'education', 'municipal', 'energy'];
  }
}

// ============================================================================
// DETECTORES DE TIPOLOGÍA
// ============================================================================

/** Patrones para detectar topónimos */
const TOPONYM_PATTERNS = [
  // Parajes y sitios
  /\b(paraje|pago|partido|sitio)\b/i,
  // Orografía
  /\b(cerro|loma|colina|alto|cabezo|peñón)\b/i,
  /\b(sierra|peña|peñasco|risco|tajo|cuesta|ladera)\b/i,
  /\b(puerto|collado|portillo)\b/i,
  /\b(cueva|sima|caverna)\b/i,
  /\b(cañón|garganta|desfiladero)\b/i,
  // Edificaciones rurales
  /\b(cortijo|cortijada|caserío|venta|molino)\b/i,
  /\b(hacienda|finca|huerta)\b/i,
  /\b(ermita|santuario|capilla)\b/i,
  /\b(castillo|atalaya|mirador)\b/i,
  // Hidrografía y cuerpos de agua
  /\b(arroyo|rambla|barranco)\b/i,
  /\b(fuente|manantial|aljibe)\b/i,
  /\b(laguna|embalse|pantano|presa|balsa|charca)\b/i,
  // Agropecuario / vías pecuarias
  /\b(era|eras|ejido)\b/i,
  /\b(dehesa|prado|majada)\b/i,
  /\b(cañada|vereda|cordel|colada)\b/i,
  // Llanuras y depresiones
  /\b(llano|vega|hoya)\b/i,
  // Vegetación
  /\b(pinar|encinar|alcornocal)\b/i,
  // Asentamientos menores
  /\b(aldea|pedanía|barriada)\b/i,
  // Fix B: núcleos urbanos y urbanizaciones sin sufijo rural clásico
  /\b(urbanizaci[oó]n|residencial|colonia)\b/i,
  /\b(n[uú]cleo|barrio|poblado|diseminado)\b/i,
  // Fix B: núcleos urbanos y urbanizaciones (municipios plurinucleares sin sufijo clásico)
  /\b(urbanizaci[oó]n|residencial|colonia)\b/i,
  /\b(n[uú]cleo|barrio|poblado|diseminado)\b/i,
  // Fix B: núcleos urbanos y urbanizaciones sin sufijo clásico (Los Gallardos)
  /\b(urbanizaci[oó]n|residencial|colonia)\b/i,
  /\b(n[uú]cleo|barrio|poblado|diseminado)\b/i,
  // Infraestructura
  /\b(puente)\b/i,
];

/** Patrones para detectar instalaciones deportivas */
const SPORTS_PATTERNS = [
  /\b(piscina|nataci[oó]n)\b/i,
  /\b(campo|estadio|f[uú]tbol)\b/i,
  /\b(polideportivo|pabellón|pabell[oó]n)\b/i,
  /\b(pista|cancha|frontón)\b/i,
  /\b(gimnasio)\b/i
];

/** Patrones para detectar infraestructura telecom */
const TELECOM_PATTERNS = [
  /\b(antena|torre)\b/i,
  /\b(movistar|vodafone|orange|yoigo|masmovil)\b/i,
  /\b(telecom|telecomunicaci[oó]n)\b/i,
  /\b(repetidor|bts|estaci[oó]n base)\b/i
];

/** Operadores de telecomunicaciones conocidos */
const TELECOM_OPERATORS = ['movistar', 'vodafone', 'orange', 'yoigo', 'masmovil'];

/** 
 * Patrones para detectar cauces hidrográficos (elementos lineales)
 * Ríos, arroyos, ramblas, barrancos - NO son infraestructuras puntuales
 * Deben geocodificarse via NGA (Nomenclátor Geográfico) no DERA Hidrografía
 */
const WATERCOURSE_PATTERNS = [
  /\b(r[íi]o)\b/i,
  /\b(arroyo)\b/i,
  /\b(rambla)\b/i,
  /\b(barranco|barranquillo)\b/i,
  /\b(regato|reguero)\b/i,
  /\b(caz|acequia)\b/i,
  /\b(cañada)\b/i,  // Vías pecuarias que siguen cauces
];

/**
 * Detecta si un texto parece ser un topónimo del NGA
 * 
 * Cubre: orografía (sierra, cerro, peña, cueva), hidrografía (laguna, embalse),
 * edificaciones rurales (ermita, cortijo, castillo), vías pecuarias (cañada, vereda),
 * agropecuario (dehesa, era), vegetación (pinar, encinar), infraestructura (puente),
 * asentamientos menores (aldea, pedanía) y parajes genéricos.
 * 
 * @param text - Texto a analizar
 * @returns true si parece topónimo
 */
export function looksLikeToponym(text: string): boolean {
  return TOPONYM_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * Detecta si es una instalación deportiva
 * 
 * @param type - Tipo de infraestructura
 * @param name - Nombre de la infraestructura
 * @returns true si es deportiva
 */
export function isSportsFacility(type: InfrastructureType, name: string): boolean {
  if (type === InfrastructureType.SPORTS) return true;
  return SPORTS_PATTERNS.some(pattern => pattern.test(name));
}

/**
 * Detecta si es infraestructura de telecomunicaciones
 * 
 * @param name - Nombre a analizar
 * @returns true si es telecom
 */
export function isTelecomInfrastructure(name: string): boolean {
  return TELECOM_PATTERNS.some(pattern => pattern.test(name));
}

/**
 * Detecta si es un cauce hidrográfico (elemento lineal)
 * 
 * Los cauces (ríos, arroyos, ramblas, barrancos) son geometrías LINEALES
 * que NO existen en DERA Hidrografía como infraestructuras puntuales.
 * Deben geocodificarse via NGA (Nomenclátor Geográfico de Andalucía)
 * que tiene topónimos de hidrografía con coordenadas del centroide.
 * 
 * Impacto: 12 registros en PTEL Tíjola sin geocodificar por falta de esta detección
 * 
 * @param name - Nombre de la infraestructura
 * @returns true si es un cauce hidrográfico
 * 
 * @example
 * isWatercourse('Río Almanzora') // true
 * isWatercourse('Rambla de Oria') // true
 * isWatercourse('Depósito Municipal') // false
 * isWatercourse('EDAR Tíjola') // false
 */
export function isWatercourse(name: string): boolean {
  return WATERCOURSE_PATTERNS.some(pattern => pattern.test(name));
}

/**
 * Extrae operador de telecomunicaciones del nombre
 * 
 * @param name - Nombre de la infraestructura
 * @returns Nombre del operador o null
 */
export function extractOperator(name: string): string | null {
  const nameLower = name.toLowerCase();
  return TELECOM_OPERATORS.find(op => nameLower.includes(op)) || null;
}

// ============================================================================
// MATCHING DE TORRES
// ============================================================================

/**
 * Tipo para torre de telecomunicaciones de Overpass/OSM
 */
export interface TelecomTower {
  lon?: number;
  lat?: number;
  name?: string;
  tags?: { 
    operator?: string;
    [key: string]: string | undefined;
  };
}

/**
 * Busca torre de telecomunicaciones que coincida con topónimo o nombre
 * 
 * @param towers - Array de torres candidatas
 * @param address - Dirección de referencia
 * @param name - Nombre de la infraestructura
 * @returns Torre coincidente o null
 */
export function findMatchingTower(
  towers: TelecomTower[],
  address: string,
  name: string
): TelecomTower | null {
  // Si solo hay una torre, retornarla
  if (towers.length === 1) {
    return towers[0];
  }

  // Intentar match por nombre de operador
  const operator = extractOperator(name);
  if (operator) {
    const byOperator = towers.find(t => 
      t.tags?.operator?.toLowerCase().includes(operator) ||
      t.name?.toLowerCase().includes(operator)
    );
    if (byOperator) return byOperator;
  }

  // Si no hay match específico, retornar la primera
  return towers[0];
}

// Re-export tipos
export type { InfraTipologia, InfrastructureCategory };
